import React from 'react';
import { useFlaggedTransactions } from 'hooks/flaggedTransactions';
import { AskRequest, AskRequestComment, AskRequestStatus, ReportRow } from '@hone-automation/common';
import { shallow } from 'zustand/shallow';
import type { PropsType } from './MarkAsSolvedButton';
import { usePatchQueueRequestMutation } from 'hooks/queue';

// Define the expected props for the HOC
export type WithAskRequestsProps = {
  txId: string;
  locationId: string;
  activeTab: AskRequestStatus;
  isSideView?: boolean;
  currentStatus?: AskRequestStatus;
};

const withAskRequests = <P extends WithAskRequestsProps>(
  WrappedComponent: ({ onSubmit, isPending }: PropsType) => React.JSX.Element
) => {
  return function WithAskRequests(props: P) {
    const { isSideView, txId } = props;

    const { setActiveTransaction, setAnimateTransaction, setTransactionBeingFlagged, transactionBeingFlagged } =
      useFlaggedTransactions(
        state => ({
          setActiveTransaction: state.setActiveTransaction,
          setTransactionBeingFlagged: state.setTransactionBeingFlagged,
          setAnimateTransaction: state.setAnimatedTransaction,
          transactionBeingFlagged: state.transactionBeingFlagged,
        }),
        shallow
      );

    const { isPending, mutate } = usePatchQueueRequestMutation((trx: AskRequest | undefined) => {
      if (!trx) return;
      isSideView ? setActiveTransaction(trx) : setTransactionBeingFlagged({ ...transactionBeingFlagged, flagged: trx });
    });

    const markAsSolved = async ({ comment }: { comment: string }) => {
      setAnimateTransaction('Closed');
      mutate({ id: txId, comment, status: 'Closed' });
    };

    // Extract only the necessary props for the wrapped component
    const wrappedComponentProps = {
      isPending: isPending,
      onSubmit: markAsSolved,
    } as PropsType;

    return <WrappedComponent {...wrappedComponentProps} />;
  };
};

export default withAskRequests;
